/**
 * Build a sliding-window page list: [1 ... (p-1) p (p+1) ... N]
 *
 * - `siblings` controls how many pages to show on each side of the current page
 * - `boundaries` controls how many pages are always shown at the start/end
 * - The literal string `"..."` represents an ellipsis gap
 */
export function buildPageList(
  total: number,
  page: number,
  siblings = 1,
  boundaries = 1,
): (number | "...")[] {
  const totalNumbers = siblings * 2 + boundaries * 2 + 3;
  if (total <= totalNumbers) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const left = Math.max(page - siblings, boundaries + 2);
  const right = Math.min(page + siblings, total - boundaries - 1);

  const result: (number | "...")[] = [];
  for (let i = 1; i <= boundaries; i++) result.push(i);
  if (left > boundaries + 2) {
    result.push("...");
  } else {
    for (let i = boundaries + 1; i < left; i++) result.push(i);
  }
  for (let i = left; i <= right; i++) result.push(i);
  if (right < total - boundaries - 1) {
    result.push("...");
  } else {
    for (let i = right + 1; i <= total - boundaries; i++) result.push(i);
  }
  for (let i = total - boundaries + 1; i <= total; i++) result.push(i);

  return result;
}

export const totalPagesOf = (count: number, limit: number) =>
  Math.max(1, Math.ceil(count / limit));
